import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { where } from 'firebase/firestore';
import {
  UsersApiService,
  VehiclesApiService,
  RunsApiService,
  LocationsApiService,
  SubLocationsApiService,
  ProcessTypesApiService,
  ProcessTypeStatusesApiService,
  SettingsVehicleChecksApiService,
} from 'core/api';
import { Role } from 'core/config/roles';
import { IUserDao } from 'core/api/types/users.interface';
import { IVehicleDao } from 'core/api/types/vehicles.interface';
import { IRunDao } from 'core/api/types/runs.interface';
import { ILocationDao } from 'core/api/types/locations.interface';
import { ISubLocationDao } from 'core/api/types/sub-locations.interface';
import { IProcessTypeDao } from 'core/api/types/process-types.interface';
import { IProcessTypeStatusDao } from 'core/api/types/process-type-statuses.interface';
import { ISettingsVehicleCheckDao } from 'core/api/types/settings-vehicle-checks.interface';
import { IDeliveryMeta } from 'core/api/meta/meta-api-interface';
import MetaApiService from 'core/api/meta/meta-api.service';

interface SharedDataContextType {
  drivers: IUserDao[] | null;
  vehicles: IVehicleDao[] | null;
  runs: IRunDao[] | null;
  locations: ILocationDao[] | null;
  subLocations: ISubLocationDao[] | null;
  processTypes: IProcessTypeDao[] | null;
  processTypeStatuses: IProcessTypeStatusDao[] | null;
  settingsVehicleChecks: ISettingsVehicleCheckDao[] | null;
  deliveryMeta: IDeliveryMeta | null;
  loading: {
    drivers: boolean;
    vehicles: boolean;
    runs: boolean;
    locations: boolean;
    subLocations: boolean;
    processTypes: boolean;
    processTypeStatuses: boolean;
    settingsVehicleChecks: boolean;
    deliveryMeta: boolean;
  };
}

const SharedDataContext = createContext<SharedDataContextType | undefined>(undefined);

export const SharedDataProvider = ({ children }: { children: ReactNode }) => {
  const [drivers, setDrivers] = useState<IUserDao[] | null>(null);
  const [vehicles, setVehicles] = useState<IVehicleDao[] | null>(null);
  const [runs, setRuns] = useState<IRunDao[] | null>(null);
  const [locations, setLocations] = useState<ILocationDao[] | null>(null);
  const [subLocations, setSubLocations] = useState<ISubLocationDao[] | null>(null);
  const [processTypes, setProcessTypes] = useState<IProcessTypeDao[] | null>(null);
  const [processTypeStatuses, setProcessTypeStatuses] = useState<IProcessTypeStatusDao[] | null>(null);
  const [settingsVehicleChecks, setSettingsVehicleChecks] = useState<ISettingsVehicleCheckDao[] | null>(null);
  const [deliveryMeta, setDeliveryMeta] = useState<IDeliveryMeta | null>(null);

  const [loading, setLoading] = useState({
    drivers: true,
    vehicles: true,
    runs: true,
    locations: true,
    subLocations: true,
    processTypes: true,
    processTypeStatuses: true,
    settingsVehicleChecks: true,
    deliveryMeta: true,
  });

  useEffect(() => {
    // A logistics driver may carry the role as a singular `role` field or in the
    // `roles` array, so subscribe to both and merge (deduped by uid).
    let byRole: IUserDao[] = [];
    let byRoles: IUserDao[] = [];
    let roleLoaded = false;
    let rolesLoaded = false;

    const merge = () => {
      const map = new Map<string, IUserDao>();
      [...byRole, ...byRoles].forEach((user) => map.set(user.uid, user));
      setDrivers(Array.from(map.values()));
      if (roleLoaded && rolesLoaded) {
        setLoading((prev) => ({ ...prev, drivers: false }));
      }
    };

    const unsubscribeRole = UsersApiService.onCollectionSnapshot(
      (snapshot) => {
        byRole = snapshot.docs.map((doc) => doc.data());
        roleLoaded = true;
        merge();
      },
      () => {
        roleLoaded = true;
        merge();
      },
      [where('role', '==', Role.LOGISTICS_DRIVER)]
    );

    const unsubscribeRoles = UsersApiService.onCollectionSnapshot(
      (snapshot) => {
        byRoles = snapshot.docs.map((doc) => doc.data());
        rolesLoaded = true;
        merge();
      },
      () => {
        rolesLoaded = true;
        merge();
      },
      [where('roles', 'array-contains', Role.LOGISTICS_DRIVER)]
    );

    return () => {
      unsubscribeRole();
      unsubscribeRoles();
    };
  }, []);

  useEffect(() => {
    const constraints = [where('active', '==', true)];
    const unsubscribe = VehiclesApiService.onCollectionSnapshot(
      (snapshot) => {
        setVehicles(snapshot.docs.map((doc) => doc.data()));
        setLoading((prev) => ({ ...prev, vehicles: false }));
      },
      () => setLoading((prev) => ({ ...prev, vehicles: false })),
      constraints
    );
    return () => unsubscribe();
  }, []);

  // Load active runs once
  useEffect(() => {
    const constraints = [where('active', '==', true)];
    const unsubscribe = RunsApiService.onCollectionSnapshot(
      (snapshot) => {
        setRuns(snapshot.docs.map((doc) => doc.data()));
        setLoading((prev) => ({ ...prev, activeRuns: false }));
      },
      () => setLoading((prev) => ({ ...prev, activeRuns: false })),
      constraints
    );
    return () => unsubscribe();
  }, []);

  // Load locations once
  useEffect(() => {
    const unsubscribe = LocationsApiService.onCollectionSnapshot(
      (snapshot) => {
        setLocations(snapshot.docs.map((doc) => doc.data()));
        setLoading((prev) => ({ ...prev, locations: false }));
      },
      () => setLoading((prev) => ({ ...prev, locations: false }))
    );
    return () => unsubscribe();
  }, []);

  // Load sub-locations once
  useEffect(() => {
    const constraints = [where('active', '==', true)];
    const unsubscribe = SubLocationsApiService.onCollectionSnapshot(
      (snapshot) => {
        setSubLocations(snapshot.docs.map((doc) => doc.data()));
        setLoading((prev) => ({ ...prev, subLocations: false }));
      },
      () => setLoading((prev) => ({ ...prev, subLocations: false })),
      constraints
    );
    return () => unsubscribe();
  }, []);

  // Load process types once
  useEffect(() => {
    const constraints = [where('active', '==', true)];
    const unsubscribe = ProcessTypesApiService.onCollectionSnapshot(
      (snapshot) => {
        setProcessTypes(snapshot.docs.map((doc) => doc.data()));
        setLoading((prev) => ({ ...prev, processTypes: false }));
      },
      () => setLoading((prev) => ({ ...prev, processTypes: false })),
      constraints
    );
    return () => unsubscribe();
  }, []);

  // Load process type statuses once
  useEffect(() => {
    const constraints = [where('active', '==', true)];
    const unsubscribe = ProcessTypeStatusesApiService.onCollectionSnapshot(
      (snapshot) => {
        setProcessTypeStatuses(snapshot.docs.map((doc) => doc.data()));
        setLoading((prev) => ({ ...prev, processTypeStatuses: false }));
      },
      () => setLoading((prev) => ({ ...prev, processTypeStatuses: false })),
      constraints
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const constraints = [where('active', '==', true)];
    const unsubscribe = SettingsVehicleChecksApiService.onCollectionSnapshot(
      (snapshot) => {
        setSettingsVehicleChecks(snapshot.docs.map((doc) => doc.data()));
      },
      () => setLoading((prev) => ({ ...prev, settingsVehicleChecks: false })),
      constraints
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = MetaApiService.subscribeToMeta(
      'deliveries',
      (doc: IDeliveryMeta) => {
        setDeliveryMeta(doc);
        setLoading((prev) => ({ ...prev, deliveryMeta: false }));
      },
      () => setLoading((prev) => ({ ...prev, deliveryMeta: false }))
    );
    return () => unsubscribe();
  }, []);

  return (
    <SharedDataContext.Provider
      value={{
        drivers,
        vehicles,
        runs,
        locations,
        subLocations,
        processTypes,
        processTypeStatuses,
        settingsVehicleChecks,
        loading,
        deliveryMeta,
      }}
    >
      {children}
    </SharedDataContext.Provider>
  );
};

export const useSharedData = () => {
  const context = useContext(SharedDataContext);

  if (!context) {
    throw new Error('useSharedData must be used within SharedDataProvider');
  }

  return context;
};
